import { useCallback, useEffect, useMemo, useState } from 'react';

import { RoleScope } from '../../../../../../typings/enums';
import { getRoleSelectOptions } from '../../../../../helpers/roles';
import useAssignableRoles from '../../../../../hooks/api/roles/useAssignableRoles';
import useRoles from '../../../../../hooks/api/roles/useRoles';
import type { SelectOption } from '../../../../../types';

const DEFAULT_SCOPES = new Set([RoleScope.Team]);

type UseSelectRolesProps = {
  selectedRoleId?: number;
  scopes?: Set<RoleScope>;
  teamId?: number;
};

/**
 * Hook which provides a list of roles and manages the state
 * of the selected role.
 *
 * Note that this will always auto-select the first role
 * when the roles load. Feel free to change this if you
 * need to, so long as the default behavior is to select
 * the first role.
 *
 * This can become a controlled hook if you pass in
 * a `selectedRoleId` prop.
 *
 * By default, this will return TEAM SCOPED ROLES.
 * If you need to get org-scoped roles (or both), you can pass in
 * a Set of RoleScope values.
 *
 * @param {UseSelectRolesProps} options - The options for the hook.
 * @param {number} [options.selectedRoleId] - The ID of the role to be initially selected.
 * @param {Set<RoleScope>} [options.scopes] - Filter to only include roles of this scope (defaults to Team)
 * @param {number} [options.teamId] - The ID of the team to filter roles for.
 */
const useSelectRoles = ({
  selectedRoleId,
  scopes = DEFAULT_SCOPES,
  teamId,
}: UseSelectRolesProps = {}) => {
  const { data: roles, isLoading } = useRoles();
  const { data: assignableRoles, isLoading: isLoadingAssignableRoles } = useAssignableRoles();
  const [selectedRole, setSelectedRole] = useState<SelectOption<number> | null>(null);
  //  Memoized so that we don't sort/filter every render.
  const roleOptions = useMemo(() => getRoleSelectOptions(roles || [], scopes, assignableRoles, teamId),
    [roles, scopes, assignableRoles, teamId],
  );
  const onChangeRole = useCallback((role: SelectOption<number> | null) => setSelectedRole(role), [setSelectedRole]);
  //  This is used after we create a team to reset the selected role to the first role option.
  const resetSelectedRole = useCallback(() => setSelectedRole(roleOptions[0] ?? null), [setSelectedRole, roleOptions]);

  //  One-time effect to set the selected role to the first role option once they load
  useEffect(() => {
    if (roleOptions?.length && !selectedRole) {
      resetSelectedRole();
    }
  }, [roleOptions, resetSelectedRole, selectedRole]);

  //  If using this hook as a controlled component, we reset the selected role to the selected role ID if selectedRoleId changes.
  useEffect(() => {
    if (selectedRoleId) {
      setSelectedRole(roleOptions.find(role => role.value === selectedRoleId) ?? null);
    }
  }, [selectedRoleId, roleOptions]);

  return {
    roleOptions,
    isLoading: isLoading && isLoadingAssignableRoles,
    selectedRole,
    onChangeRole,
    resetSelectedRole,
  };
};

export default useSelectRoles;
